import { useFirstDataCardPayment } from '../../../payment/hooks/use-first-data-card-payment';
import { useRequestApplePayPayment } from '../../../payment/hooks/use-request-apple-pay-payment';
import { useRequestBraintreePayment } from '../../../payment/hooks/use-request-braintree-payment';
import { useRequestCashAppPayment } from '../../../payment/hooks/use-request-cash-app-payment';
import { useRequestGooglePayPayment } from '../../../payment/hooks/use-request-google-pay-payment';
import { useCheckoutState } from '../use-checkout-state';

/**
 * Aggregates and returns payment details from various payment providers based on the current checkout state.
 *
 * This custom React hook determines which payment method is currently selected in the checkout process
 * (e.g., Braintree, Cash App, Google Pay, Apple Pay, or First Data Card) and invokes the corresponding
 * payment hook to retrieve the necessary payment details for that provider.
 *
 * The selection logic is based on flags and values from the checkout state, such as `isBraintreePayment`,
 * `isCashAppPayment`, `isGooglePayPayment`, and `isApplePayPayment`. If none of these are active, it defaults
 * to using the First Data Card payment details.
 *
 * The hook also handles the case where payment methods need to be reloaded, ensuring the correct payment values
 * are used for each provider.
 *
 * @returns The payment details object from the currently selected payment provider's hook.
 */
export const useRequestPaymentDetails = () => {
  const {
    billingCountry,
    showReloadPaymentMethods,
    paymentValues,
    reloadPaymentValues,
    isPaymentProcessor,
    isPaymentType,
    totals,
    // TODO: We can remove the dependency on this value.
    // Its only being used in the apple pay hook to calculate the total
    serverOrder,
  } = useCheckoutState();
  const cardPayment = useFirstDataCardPayment({
    firsDataCard: {
      paymentValues: showReloadPaymentMethods ? reloadPaymentValues : paymentValues,
    },
  });
  const cashAppPayment = useRequestCashAppPayment({
    cashapp: { total: totals?.grandTotal || 0, tax: totals?.taxCents ?? 0 },
  });
  const braintreePayment = useRequestBraintreePayment({});
  const googlePayment = useRequestGooglePayPayment({
    googlePay: { billingCountry, total: totals?.grandTotal ?? 0, reloadAmount: 0 },
  });
  const applePayment = useRequestApplePayPayment({
    applePay: {
      serverOrder,
      tip: totals?.tipAmount ?? 0,
      reloadAmount: 0,
      billingCountry,
    },
  });

  if (isPaymentProcessor.braintree) {
    return braintreePayment;
  }

  if (isPaymentType.cashApp) {
    return cashAppPayment;
  }

  if (isPaymentType.googlePay) {
    return googlePayment;
  }

  if (isPaymentType.applePay) {
    return applePayment;
  }

  return cardPayment;
};
